import BottomNavBar from '@/components/modules/navigation/BottomNavBar/BottomNavBar';
import FooterNavigation from '@/components/modules/navigation/FooterNavigation';
import FooterNavigationContainer from '@/components/modules/navigation/FooterNavigationContainer';
import NavBar from '@/components/modules/navigation/navbar/NavBar/NavBar';
import { SCREEN_NAME } from '@/constants/analytics';
import {
  MEGA_MENU_NAVIGATION_FEATURE_FLAG_PLACE_ONLY,
  MEGA_MENU_NAVIGATON_EXCLUDING_PLACE,
} from '@/constants/experimentConstants';
import Footer from '@/features/footer/Footer';
import MegaMenu from '@/features/mega-menu/MegaMenu';
import { useMegaMenuContext } from '@/features/mega-menu/MegaMenu.hooks';
import { HideOnScrollWrapper } from '@/features/search-header/SearchHeader';
import { classnames } from '@/helpers';
import { getScreenNameFromPath } from '@/helpers/analyticsTS';
import { useAppSelector } from '@/hooks';
import { useConfidenceFlag } from '@/hooks/useConfidenceFlag';
import useMobileView from '@/hooks/useMobileView';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { Fragment } from 'react/jsx-runtime';
import ContainerLayout from '../ContainerLayout';
import { getFooterDisplayOption } from '../helpers';
import { PageViewLayout as PageViewLayoutProps } from '../types';

const PageViewLayout = ({
  children,
  footerDisplayOption,
  wrapperClass,
  headerNavigationContainerClassName,
  type,
  hideBottomNavBar = false,
  ...navbarProps
}: PageViewLayoutProps) => {
  const { isMobileView } = useMobileView();
  const flags = useAppSelector((state) => state.flags);
  const megaMenuContext = useMegaMenuContext();
  const location = useLocation();
  const isWhitelabel = useAppSelector((state) => state?.settings.isWhitelabel);
  const isPlacePage = getScreenNameFromPath(location.pathname) === SCREEN_NAME.COMPANY_DETAILS;

  const megaMenuExcludingPlaceFlag = useConfidenceFlag(
    MEGA_MENU_NAVIGATON_EXCLUDING_PLACE.name,
    MEGA_MENU_NAVIGATON_EXCLUDING_PLACE.defaultValue,
    !isPlacePage && Boolean(flags[MEGA_MENU_NAVIGATON_EXCLUDING_PLACE.name]?.payload),
  ).enabled;

  let isMegaMenuFeature = false;

  if (!navbarProps.temporaryForceOldHeader) {
    if (isPlacePage) {
      isMegaMenuFeature = flags[MEGA_MENU_NAVIGATION_FEATURE_FLAG_PLACE_ONLY]?.value === 'on';
    } else {
      isMegaMenuFeature = megaMenuExcludingPlaceFlag;
    }
  }

  const showBottomNavBar = type === 'mainView' && !hideBottomNavBar && isMobileView;
  const showMegaMenuNavigation = isMegaMenuFeature && !isWhitelabel;

  const defaultNavBarHeight = (() => {
    /**
     * If defaultNavBarHeight is provided via props, use that value
     */
    if (navbarProps.defaultNavBarHeight) {
      return navbarProps.defaultNavBarHeight;
    }
    /**
     * If mega menu navigation is shown
     * set defaultNavBarHeight to 120 for mobile view and 128 for desktop view
     * to match the height of the mega menu navigation to avoid CLS issues
     */
    if (showMegaMenuNavigation && type === 'mainView') {
      return isMobileView ? 120 : 128;
    }

    return undefined;
  })();

  return (
    <div className={wrapperClass}>
      <NavBar
        navClass={classnames(headerNavigationContainerClassName)}
        {...navbarProps}
        defaultNavBarHeight={defaultNavBarHeight}
        type={type}
        bottomSlot={(() => {
          if (navbarProps.bottomSlot !== undefined) {
            return navbarProps.bottomSlot;
          }
          if (isMegaMenuFeature && !isWhitelabel) {
            const megaMenu = (
              <MegaMenu
                {...megaMenuContext}
                renderNavigationContainer={(props) => {
                  const ref = props.navigationContainerRef.current;
                  return ref ? (
                    createPortal(<MegaMenu.Categories {...props} />, ref)
                  ) : (
                    /**
                     * need to render the navigation before the ref is mounted to make sure navigation appears on initial render
                     */
                    <nav id={props.navigationContainerId} className="mega-menu-navigation-container">
                      <MegaMenu.Categories {...props} />
                    </nav>
                  );
                }}
              />
            );

            if (megaMenuContext.activeTab) {
              return megaMenu;
            }
            /** if ref is not mounted use 56px for now for for Mega menu tabs (update if component changes) */
            const height = megaMenuContext.navRef.current?.getBoundingClientRect()?.height ?? 56;
            return <HideOnScrollWrapper height={height}>{megaMenu}</HideOnScrollWrapper>;
          }
          return undefined;
        })()}
        floatingSlot={
          navbarProps.floatingSlot ?? (isMegaMenuFeature && !isWhitelabel) ? (
            <Fragment>{megaMenuContext.renderNavigationContainer(megaMenuContext)}</Fragment>
          ) : null
        }
      />
      {children}
      {type === 'mainView' && (
        <Fragment>
          {isMegaMenuFeature && (
            <ContainerLayout
              className={classnames(
                footerDisplayOption?.from && footerDisplayOption.to && getFooterDisplayOption(footerDisplayOption),
              )}>
              <Footer />
            </ContainerLayout>
          )}
          {!isMegaMenuFeature && (
            <FooterNavigationContainer>
              <FooterNavigation />
            </FooterNavigationContainer>
          )}
        </Fragment>
      )}

      {showBottomNavBar && !megaMenuContext.activeTab && <BottomNavBar source={navbarProps.source} />}
    </div>
  );
};

export default PageViewLayout;
